import { getCrmCardColor } from "../../theme/crmColors";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Chip, CircularProgress, IconButton, InputAdornment, Paper, TextField, Tooltip, Typography } from "@material-ui/core";
import { Add, ArrowBack, DeleteOutline, EditOutlined, ForumOutlined, Refresh, Reply, Schedule, Search, ViewWeekOutlined } from "@material-ui/icons";

import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../../components/ConfirmationModal";
import TagModal from "../../components/TagModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { fetchKanbanStages, fetchKanbanTickets } from "../../services/kanban";
import KanbanTheme from "../../theme/kanbanTheme";

const useStyles = makeStyles(theme => ({
  root: {
    height: "calc(100% - 48px)", minHeight: 0, minWidth: 0,
    display: "flex", flexDirection: "column", padding: theme.spacing(3),
    background: theme.crm.background, color: theme.crm.text,
    [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) }
  },
  header: { display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" },
  back: { border: `1px solid ${theme.crm.border}`, borderRadius: 10, color: theme.crm.text },
  heading: { display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 150 },
  title: { fontSize: 24, fontWeight: 750, letterSpacing: "-.4px" },
  count: { background: theme.crm.raised, color: theme.crm.muted, fontWeight: 700 },
  add: { borderRadius: 9, textTransform: "none", fontWeight: 700, boxShadow: "none", minHeight: 40 },
  toolbar: { display: "flex", alignItems: "center", gap: 12, marginBottom: 18 },
  search: { maxWidth: 440, width: "100%", "& .MuiOutlinedInput-root": { borderRadius: 10, background: getCrmCardColor(theme) } },
  scroll: { minHeight: 0, flex: 1, overflow: "auto", padding: "2px 4px 16px 0", ...theme.scrollbarStyles },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 320px), 1fr))", gap: 14, alignItems: "start" },
  card: { minWidth: 0, borderRadius: 12, border: `1px solid ${theme.crm.border}`, background: getCrmCardColor(theme), overflow: "hidden", boxShadow: "none" },
  cardTop: { display: "flex", alignItems: "flex-start", gap: 10, padding: "18px 18px 12px" },
  marker: { width: 12, height: 12, borderRadius: 4, flexShrink: 0, marginTop: 5 },
  name: { flex: 1, minWidth: 0, fontWeight: 750, fontSize: 17, lineHeight: 1.4, overflowWrap: "anywhere", color: theme.crm.text },
  activeCount: { padding: "0 18px 16px 40px", display: "flex", alignItems: "baseline", gap: 6, color: theme.crm.muted, fontSize: 12 },
  countNumber: { color: theme.crm.text, fontSize: 20, fontWeight: 750 },
  rules: { margin: "0 18px", padding: "13px 0", borderTop: `1px solid ${theme.crm.border}`, display: "grid", gap: 10, minHeight: 86 },
  rule: { display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: theme.crm.muted, minWidth: 0, "& svg": { width: 16, height: 16, flexShrink: 0 } },
  ruleText: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  automation: { color: theme.crm.accent },
  cardActions: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 12px", background: theme.uiPalette === "github" && theme.palette.type === "dark" ? theme.crm.surface : theme.crm.inset, borderTop: `1px solid ${theme.crm.border}` },
  edit: { textTransform: "none", fontWeight: 650, color: theme.crm.text, borderRadius: 8 },
  delete: { color: theme.crm.muted, "&:hover": { color: theme.kanbanDark ? "#FFB0B8" : "#B42336" } },
  state: { padding: theme.spacing(5, 2), textAlign: "center", color: theme.crm.muted, borderRadius: 12, background: theme.crm.surface, "& > svg": { fontSize: 36, marginBottom: 10 }, "& > p": { marginBottom: 16 } },
  error: { padding: "10px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", border: `1px solid ${theme.crm.border}`, background: theme.crm.surface, color: theme.crm.text, borderRadius: 10, "& span": { flex: 1 } },
  updating: { marginLeft: "auto", flexShrink: 0 }
}));

const normalizeSearch = value => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase();

const TagsKanbanContent = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const companyId = user?.companyId;
  const [stages, setStages] = useState([]);
  const [laneCounts, setLaneCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const mountedRef = useRef(false);
  const inFlightRef = useRef(false);
  const queuedRef = useRef(false);
  const timerRef = useRef(null);
  const stagesRef = useRef(stages);
  stagesRef.current = stages;

  const loadData = useCallback(async ({ quiet = false } = {}) => {
    if (!mountedRef.current) return;
    if (inFlightRef.current) { queuedRef.current = true; return; }
    inFlightRef.current = true;
    if (!quiet) setLoading(true);
    else setRefreshing(true);
    try {
      const [stageResponse, ticketResponse] = await Promise.all([
        fetchKanbanStages(), fetchKanbanTickets({ filters: {} })
      ]);
      if (!mountedRef.current) return;
      setStages((stageResponse.data.lista || []).filter(stage => Number(stage.kanban) === 1));
      setLaneCounts(ticketResponse.data.laneCounts || {});
      setError("");
    } catch (err) {
      if (mountedRef.current) setError("Não foi possível atualizar as etapas.");
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) {
        setLoading(false);
        setRefreshing(false);
        if (queuedRef.current) {
          queuedRef.current = false;
          clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => { timerRef.current = null; loadData({ quiet: true }); }, 400);
        }
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    loadData();
    return () => { mountedRef.current = false; queuedRef.current = false; clearTimeout(timerRef.current); timerRef.current = null; };
  }, [loadData]);

  useEffect(() => {
    if (!socket || !companyId) return undefined;
    const scheduleRefresh = () => {
      if (timerRef.current) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        loadData({ quiet: true });
      }, 450);
    };
    const onTagsEvent = data => {
      if ((data?.action === "create" || data?.action === "update") && Number(data.tag?.kanban) === 1) scheduleRefresh();
      if (data?.action === "delete" && stagesRef.current.some(stage => Number(stage.id) === Number(data.tagId))) scheduleRefresh();
    };
    const onKanbanEvent = data => { if (data?.action === "invalidate") scheduleRefresh(); };
    const tagChannel = `company${companyId}-tag`;
    const kanbanChannel = `company-${companyId}-kanban`;
    const ticketChannel = `company-${companyId}-ticket`;
    socket.on(tagChannel, onTagsEvent);
    socket.on(kanbanChannel, onKanbanEvent);
    socket.on(ticketChannel, scheduleRefresh);
    return () => {
      socket.off(tagChannel, onTagsEvent);
      socket.off(kanbanChannel, onKanbanEvent);
      socket.off(ticketChannel, scheduleRefresh);
      clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [companyId, loadData, socket]);

  const filteredStages = useMemo(() => stages.filter(stage => normalizeSearch(stage.name).includes(normalizeSearch(search).trim())), [search, stages]);
  const stageNames = useMemo(() => new Map(stages.map(stage => [Number(stage.id), stage.name])), [stages]);
  const openModal = stage => { setSelectedTag(stage || null); setTagModalOpen(true); };
  const closeModal = () => { setTagModalOpen(false); setSelectedTag(null); };

  const deleteStage = async () => {
    if (!deletingTag || deletingId) return;
    const stageId = deletingTag.id;
    setDeletingId(stageId);
    try {
      await api.delete(`/tags/${stageId}`);
      if (!mountedRef.current) return;
      setStages(current => current.filter(stage => stage.id !== stageId));
      toast.success("Etapa excluída.");
      loadData({ quiet: true });
    } catch (err) {
      if (mountedRef.current) toastError(err);
    } finally {
      if (mountedRef.current) { setDeletingId(null); setDeletingTag(null); }
    }
  };

  return (
    <section className={classes.root} aria-label="Configurações do Kanban">
      <header className={classes.header}>
        <Tooltip title="Voltar ao Kanban"><IconButton className={classes.back} onClick={() => history.push("/kanban")} aria-label="Voltar ao Kanban"><ArrowBack /></IconButton></Tooltip>
        <div className={classes.heading}><Typography component="h1" className={classes.title}>Etapas do Kanban</Typography><Chip size="small" label={stages.length} className={classes.count} /></div>
        <Button className={classes.add} variant="contained" color="primary" startIcon={<Add />} onClick={() => openModal(null)}>Nova etapa</Button>
      </header>
      <div className={classes.toolbar}>
        <TextField className={classes.search} value={search} onChange={event => setSearch(event.target.value)} placeholder="Buscar etapa" variant="outlined" size="small" type="search" inputProps={{ "aria-label": "Buscar etapa" }} InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
        {refreshing && <CircularProgress size={18} className={classes.updating} aria-label="Atualizando etapas" />}
        <Tooltip title="Atualizar"><span><IconButton onClick={() => loadData({ quiet: true })} disabled={refreshing || loading} aria-label="Atualizar etapas"><Refresh /></IconButton></span></Tooltip>
      </div>
      {error && <div className={classes.error} role="alert"><span>{error}</span><Button size="small" color="primary" onClick={() => loadData()}>Tentar novamente</Button></div>}
      <div className={classes.scroll}>
        {loading ? <div className={classes.state}><CircularProgress size={30} aria-label="Carregando etapas" /></div> : (
          <>
            {!error && filteredStages.length === 0 && <Paper variant="outlined" className={classes.state}><ViewWeekOutlined /><Typography>{search ? "Nenhuma etapa encontrada." : "Nenhuma etapa criada."}</Typography><Button color="primary" variant="outlined" onClick={() => search ? setSearch("") : openModal(null)}>{search ? "Limpar busca" : "Criar etapa"}</Button></Paper>}
            <div className={classes.grid}>
              {filteredStages.map(stage => {
                const automatic = Number(stage.timeLane) > 0 && Boolean(stage.nextLaneId);
                const nextName = stageNames.get(Number(stage.nextLaneId)) || "Etapa indisponível";
                const rollbackName = stageNames.get(Number(stage.rollbackLaneId)) || "Etapa indisponível";
                const advanceLabel = automatic ? `${stage.timeLane}h → ${nextName}` : stage.nextLaneId ? `Pausado → ${nextName}` : "Avanço manual";
                const total = Number(laneCounts[String(stage.id)]) || 0;
                return (
                  <Paper key={stage.id} component="article" className={classes.card} aria-label={`Etapa ${stage.name}`}>
                    <div className={classes.cardTop}><span className={classes.marker} style={{ backgroundColor: stage.color }} /><Typography component="h2" className={classes.name}>{stage.name}</Typography></div>
                    <div className={classes.activeCount}><strong className={classes.countNumber}>{total}</strong><span>{total === 1 ? "atendimento ativo" : "atendimentos ativos"}</span></div>
                    <div className={classes.rules}>
                      <div className={`${classes.rule} ${automatic ? classes.automation : ""}`}><Schedule /><Tooltip title={advanceLabel}><span className={classes.ruleText}>{advanceLabel}</span></Tooltip></div>
                      <div className={classes.rule}><Reply /><Tooltip title={stage.rollbackLaneId ? `Ao responder → ${rollbackName}` : "Ao responder, mantém a etapa"}><span className={classes.ruleText}>{stage.rollbackLaneId ? `Ao responder → ${rollbackName}` : "Resposta mantém a etapa"}</span></Tooltip></div>
                      {stage.greetingMessageLane && <div className={classes.rule}><ForumOutlined /><span>Mensagem de entrada configurada</span></div>}
                    </div>
                    <div className={classes.cardActions}>
                      <Button size="small" className={classes.edit} startIcon={<EditOutlined />} onClick={() => openModal(stage)}>Editar etapa</Button>
                      <Tooltip title="Excluir etapa"><span><IconButton size="small" className={classes.delete} disabled={deletingId === stage.id} aria-label={`Excluir etapa ${stage.name}`} onClick={() => { setDeletingTag(stage); setConfirmOpen(true); }}>{deletingId === stage.id ? <CircularProgress size={18} /> : <DeleteOutline />}</IconButton></span></Tooltip>
                    </div>
                  </Paper>
                );
              })}
            </div>
          </>
        )}
      </div>
      <ConfirmationModal title="Excluir etapa?" open={confirmOpen} onClose={setConfirmOpen} onConfirm={deleteStage}>{deletingTag ? `Excluir a etapa “${deletingTag.name}”?` : ""}</ConfirmationModal>
      {tagModalOpen && <TagModal open onClose={closeModal} onSaved={() => loadData({ quiet: true })} tagId={selectedTag?.id} kanban={1} />}
    </section>
  );
};

export default function TagsKanban() {
  return <KanbanTheme><TagsKanbanContent /></KanbanTheme>;
}
